import React, { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";

import client from "@culturehq/client";
import { Button, classnames, PlainButton, Thumbnail } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFetch, useUserActivityCreatedListener } from "app/hooks";
import { StoryTrendLink } from "app/links";
import { getName, t, Trans } from "app/utils";

import book from "images/badges/book.png";

import Accordian from "components/common/Accordian";
import { TimeDiff } from "components/common/Time";
import UserThumbnail from "components/person/UserThumbnail";
import IconsPagination from "components/common/IconsPagination";
import UsersModal from "components/common/UsersModal";

const MAX_USERS_TO_SHOW = 4;

const PendingPromptCard = ({ storyQuestion, onRemovePendingRequest }) => {
  const history = useHistory();
  const [expanded, setExpanded] = useState(false);
  const sentStory = storyQuestion.sentStories[storyQuestion.sentStories.length - 1];

  const handleDismiss = async () => {
    try {
      await client.updateStoryQuestionsRequests({
        id: sentStory.sentStoryUsers[0].id,
        dismiss: true
      });
      onRemovePendingRequest(storyQuestion);
    } catch (error) {
      if (error.status !== 404) {
        throw error;
      }
    }
  };

  const onAnswerPrompt = () => {
    if (storyQuestion.parentStoryQuestion) {
      history.push(`stories/trend/${storyQuestion.parentStoryQuestion.id}/prompt/${storyQuestion.id}#create`);
      return;
    }

    if (storyQuestion.storyQuestionType === "old_prompt") {
      history.push(`stories/trend/${storyQuestion.id}#create`);
      return;
    }

    handleDismiss();
    history.push(`stories/trend/${storyQuestion.id}`);
  };

  return (
    <div className="PendingPromptCard">
      <div className="PendingPromptCard__Header flx-row">
        <div className="flx-row">
          <Thumbnail image={sentStory.sender.avatar.thumbUrl} size="medium" />
          <div>
            <span>
              <strong>{sentStory.sender.name}</strong>
              {" "}
              <Trans>wants to see your story for this trend</Trans>
            </span>
            <p className="PendingPromptCard__Header__date">
              <TimeDiff time={sentStory.createdAt} format="%B %e at %l:%M %p" />
            </p>
          </div>
        </div>
        <div className="PendingPromptCard__Header__points">
          <img className="PendingPromptCard__Header__points-image" src={book} alt={t("Story points")} />
          <span className="PendingPromptCard__Header__points-label">+{storyQuestion.points}</span>
        </div>
      </div>
      <div className="PendingPromptCard__Body flx-row">
        <Thumbnail image={sentStory.imageUrl} size="large" />
        <div className={classnames({ expanded })}>
          <h3 className="PendingPromptCard__Body--Title">
            <StoryTrendLink storyQuestion={storyQuestion}>
              {storyQuestion.question}
            </StoryTrendLink>
          </h3>
          {sentStory.note?.length > 0 && (
            <>
              <p className="PendingPromptCard__Body--Description">&quot;{sentStory.note}&quot;</p>
              {sentStory.note.length > 205 && (
                <>
                  <br />
                  <PlainButton className="SeeMore" onClick={() => setExpanded(!expanded)}>
                    <Trans>see {expanded ? "less" : "more"}</Trans>
                  </PlainButton>
                </>
              )}
            </>
          )}
        </div>
      </div>
      <div className="PendingPromptCard__Footer flx-row">
        <div>
          <div className="PendingPromptCard__Footer__Users">
            {storyQuestion.peopleWhoAnswered.length > 0 && (
              <UsersModal
                users={storyQuestion.peopleWhoAnswered}
                loading={false}
                fetchUsers={() => {}}
                pagination={{
                  currentPage: 1,
                  totalPages: 1
                }}
                title="Who has answered the prompt?"
              >
                {onTrigger => (
                  <div className="flx-row" onClick={onTrigger}>
                    <div className="flx-row">
                      {storyQuestion.peopleWhoAnswered.map((user, index) => (
                        index < MAX_USERS_TO_SHOW && <UserThumbnail key={user.id} user={user} />
                      ))}
                      {storyQuestion.peopleWhoAnswered.length > MAX_USERS_TO_SHOW && <span className="PendingPromptCard__Footer__Users__more Thumbnail">+{storyQuestion.peopleWhoAnswered.length - MAX_USERS_TO_SHOW}</span>}
                    </div>
                    <span className="PendingPromptCard__Footer__Users__description">
                      <Trans>shared their story</Trans>
                    </span>
                  </div>
                )}
              </UsersModal>
            )}
          </div>
        </div>
        <div className="PendingPromptCard__Footer-buttons">
          <Button
            className="PendingPromptCard__Footer-buttons-inverted"
            inverted
            onClick={handleDismiss}
          >
            <Trans>Dismiss</Trans>
          </Button>
          <Button onClick={onAnswerPrompt}>
            <Trans>{(storyQuestion.parentStoryQuestion || storyQuestion.storyQuestionType === "old_prompt") ? t("+ Answer Prompt") : t("+ Join The Trend")}</Trans>
          </Button>
        </div>
      </div>
    </div>
  );
};

const PendingPrompts = () => {
  const { profile } = useProfile();
  const [page, setPage] = useState(1);

  const { error, fetching, json: { storyQuestions }, onUpdate } = useFetch(
    () => client.getStoryQuestionsRequests(),
    []
  );

  useUserActivityCreatedListener(
    useCallback(
      activity => {
        if (activity.activityType === "story_created") {
          setPage(1);
          onUpdate({
            storyQuestions: storyQuestions.filter(
              storyQuestion => storyQuestion.id !== activity.entity.question.id
            )
          });
        }
      },
      [onUpdate, storyQuestions]
    )
  );

  const changePage = newPage => {
    setPage(newPage);
  };

  const handleRemovePendingRequest = updatedStoryQuestion => {
    if (page === storyQuestions.length) {
      setPage(1);
    }
    onUpdate({
      storyQuestions: storyQuestions.filter(
        storyQuestion => storyQuestion.id !== updatedStoryQuestion.id
      )
    });
  };

  if (fetching || error || storyQuestions.length === 0) {
    return <></>;
  }

  return (
    <div className="FeedItem FeedItem__PendingPrompts">
      <div className="main-container">
        <Accordian
          icon="book-filled"
          title={t(`You have ${storyQuestions.length} prompt${storyQuestions.length > 1 ? "s" : ""} awaiting your response`)}
          className="base-menu"
          initialOpen
          titleOpen={t(`Welcome back, ${getName(profile)} 👋!`)}
          sumIcon
          exclamationIcon
        >
          <h4 className="FeedItem__PendingPrompts_Title"><Trans>You have {storyQuestions.length} prompt{storyQuestions.length > 1 ? "s" : ""} awaiting your response</Trans></h4>
          <PendingPromptCard
            onRemovePendingRequest={handleRemovePendingRequest}
            storyQuestion={storyQuestions[page - 1]}
          />
          <IconsPagination
            className="FeedItem__PendingPrompts_Pagination"
            currentPage={page}
            onClick={changePage}
            totalPages={storyQuestions.length}
          />
        </Accordian>
      </div>
    </div>
  );
};

export default PendingPrompts;
